/**
 * Checklist Actions Hook
 *
 * Wraps checklist mutations (complete, uncomplete, generateSummary,
 * generateInformation, generateExamples, findRelatedDocuments)
 * using useApiMutation for consistent error handling.
 */

import { api } from "convex/_generated/api";
import type { AuditId, Id } from "@/packages/types";
import { useApiMutation } from "@/packages/features-shared";

/* ------------------------------------------------------------------ */
/* Hook                                                                */
/* ------------------------------------------------------------------ */

export function useChecklistActions(auditId: AuditId) {
  const completeMutation = useApiMutation(
    api.checklist_mutations.completeChecklistItem,
    { name: "complete-checklist-item", silentSuccess: true },
  );
  const uncompleteMutation = useApiMutation(
    api.checklist_mutations.uncompleteChecklistItem,
    { name: "uncomplete-checklist-item", silentSuccess: true },
  );
  const generateSummaryMutation = useApiMutation(
    api.checklist_mutations.generateChecklistItemSummary,
    {
      name: "generate-checklist-summary",
      successMessage: "Summary generation started",
    },
  );
  const generateInformationMutation = useApiMutation(
    api.checklist_mutations.generateChecklistItemInformation,
    {
      name: "generate-checklist-information",
      successMessage: "Information generation started",
    },
  );
  const generateExamplesMutation = useApiMutation(
    api.checklist_mutations.generateChecklistItemExamples,
    {
      name: "generate-checklist-examples",
      successMessage: "Examples generation started",
    },
  );
  const findRelatedDocumentsMutation = useApiMutation(
    api.checklist_mutations.findRelatedDocumentsForChecklistItem,
    {
      name: "find-related-documents",
      successMessage: "Document search started",
    },
  );
  const updateDetailsMutation = useApiMutation(
    api.checklist_mutations.updateChecklistItemDetails,
    {
      name: "update-checklist-item-details",
      successMessage: "Checklist item updated",
    },
  );

  return {
    complete: (checklistItemId: Id<"checklistItems">) =>
      completeMutation.mutateAsync({ checklistItemId }),
    uncomplete: (checklistItemId: Id<"checklistItems">) =>
      uncompleteMutation.mutateAsync({ checklistItemId }),
    generateSummary: (checklistItemId: Id<"checklistItems">) =>
      generateSummaryMutation.mutateAsync({
        checklistItemId,
        auditId: auditId,
      }),
    generateInformation: (
      checklistItemId: Id<"checklistItems">,
      customInstructions?: string,
    ) =>
      generateInformationMutation.mutateAsync({
        checklistItemId,
        auditId: auditId,
        customInstructions: customInstructions || undefined,
      }),
    generateExamples: (
      checklistItemId: Id<"checklistItems">,
      customInstructions?: string,
    ) =>
      generateExamplesMutation.mutateAsync({
        checklistItemId,
        auditId: auditId,
        customInstructions: customInstructions || undefined,
      }),
    findRelatedDocuments: (checklistItemId: Id<"checklistItems">) =>
      findRelatedDocumentsMutation.mutateAsync({ checklistItemId }),
    updateDetails: (
      checklistItemId: Id<"checklistItems">,
      description: string,
      notes: string,
    ) =>
      updateDetailsMutation.mutateAsync({
        checklistItemId,
        description,
        notes,
      }),
  };
}
